import { useState, useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import { Check, ChevronLeft, ChevronRight, CalendarCheck, ImagePlus, X } from "lucide-react";
import { toast } from "sonner";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Checkbox } from "@/components/ui/checkbox";
import { Calendar } from "@/components/ui/calendar";
import api from "@/lib/api";
import Header from "@/components/Header";
import Footer from "@/components/Footer";
import { LuxButton, Eyebrow } from "@/components/lux";
import { SHOOT_TYPES, CONTACT_METHODS, HEARD_ABOUT } from "@/lib/constants";
import QuoteForm from "@/components/QuoteForm";

const STEPS = ["Package", "Date", "Time", "Details", "Review"];

function LabeledInput({ label, value, onChange, type = "text", testid, required }) {
  return (
    <div>
      <Label className="text-[#94A3B8] text-xs font-mono2 tracking-wide">{label}{required && <span className="text-[#02AACF]"> *</span>}</Label>
      <Input type={type} value={value} onChange={onChange} data-testid={testid}
        className="mt-2 bg-[#0b0d12] border-white/10 text-white focus-visible:ring-[#02AACF]" />
    </div>
  );
}

function Chips({ options, value, onChange, testidPrefix }) {
  return (
    <div className="flex flex-wrap gap-2">
      {options.map((o) => (
        <button key={o} type="button" onClick={() => onChange(o)} data-testid={`${testidPrefix}-${o.split(" ")[0].toLowerCase()}`}
          className={`text-xs font-mono2 px-3 py-2 rounded-full border transition-all ${value === o ? "bg-[#02AACF] border-[#02AACF] text-white" : "border-white/15 text-white/60 hover:border-white/40"}`}>
          {o}
        </button>
      ))}
    </div>
  );
}

export default function BookingPage() {
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [packages, setPackages] = useState([]);
  const [avail, setAvail] = useState({ blocked_dates: [], booked: [], working_days: [0,1,2,3,4,5,6], min_notice_hours: 24, max_advance_days: 120, min_date: null, max_date: null });
  const [slots, setSlots] = useState([]);
  const [step, setStep] = useState(0);
  const [quoteMode, setQuoteMode] = useState(params.get("type") === "quote" || (typeof window !== "undefined" && window.location.pathname === "/quote"));
  const [confirmation, setConfirmation] = useState(null);
  const [submitting, setSubmitting] = useState(false);

  const [form, setForm] = useState({
    package_slug: "", package_name: "", package_price: null,
    full_name: "", email: "", phone: "", preferred_contact: "Email",
    shoot_date: null, shoot_time: "", location: "", has_location: "No",
    num_people: "", num_outfits: "", shoot_type: "", purpose: "",
    instagram: "", business_name: "", heard_about: "",
    vision: "", look: "", theme: "", wardrobe: "", notes: "", reference_links: "", uploaded_inspiration: [],
    is_custom_time: false, custom_reason: "",
    agreed: false,
  });
  const [ack, setAck] = useState({ reviewed: false, terms: false, understand: false });
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const refInput = useRef();
  const [uploadingRef, setUploadingRef] = useState(false);
  const uploadRefs = async (files) => {
    if (!files?.length) return;
    if (files.length + form.uploaded_inspiration.length > 8) { toast.error("Up to 8 images total."); return; }
    setUploadingRef(true);
    const fd = new FormData();
    Array.from(files).forEach((f) => fd.append("files", f));
    try {
      const res = await api.post("/upload/reference", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setForm((f) => ({ ...f, uploaded_inspiration: [...f.uploaded_inspiration, ...res.data.urls] }));
      toast.success("Reference images uploaded");
    } catch (err) { toast.error(err?.response?.data?.detail || "Upload failed"); }
    finally { setUploadingRef(false); }
  };
  const removeRef = (url) => setForm((f) => ({ ...f, uploaded_inspiration: f.uploaded_inspiration.filter((u) => u !== url) }));

  useEffect(() => { window.scrollTo(0, 0); }, []);
  useEffect(() => {
    api.get("/packages").then((r) => {
      setPackages(r.data);
      const slug = params.get("package");
      if (slug) {
        const p = r.data.find((x) => x.slug === slug);
        if (p) { set("package_slug", p.slug); set("package_name", p.name); set("package_price", p.price); }
      }
    }).catch(() => {});
    api.get("/availability").then((r) => setAvail(r.data)).catch(() => {});
  }, []); // eslint-disable-line

  const fmt = (d) => d ? `${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,"0")}-${String(d.getDate()).padStart(2,"0")}` : null;
  const to12h = (t) => { if (!t) return ""; const [h, m] = t.split(":").map(Number); if (Number.isNaN(h)) return t; const ap = h >= 12 ? "PM" : "AM"; const hr = ((h + 11) % 12) + 1; return `${hr}:${String(m).padStart(2, "0")} ${ap}`; };

  useEffect(() => {
    if (form.shoot_date) {
      api.get("/availability/slots", { params: { date: form.shoot_date } }).then((r) => setSlots(r.data.slots)).catch(() => setSlots([]));
    }
  }, [form.shoot_date]);

  const disabledDays = useMemo(() => {
    return (day) => {
      const ds = fmt(day);
      if (avail.min_date && ds < avail.min_date) return true;
      if (avail.max_date && ds > avail.max_date) return true;
      if (!avail.working_days.includes((day.getDay() + 6) % 7)) return true; // JS 0=Sun -> 0=Mon
      if (avail.blocked_dates.includes(ds)) return true;
      return false;
    };
  }, [avail]); // eslint-disable-line

  const next = () => setStep((s) => Math.min(s + 1, STEPS.length - 1));
  const back = () => setStep((s) => Math.max(s - 1, 0));

  const canNext = () => {
    if (step === 0) return !!form.package_slug;
    if (step === 1) return !!form.shoot_date;
    if (step === 2) return !!form.shoot_time;
    if (step === 3) return form.full_name && form.email && form.phone;
    return true;
  };

  const submit = async () => {
    if (!(ack.reviewed && ack.terms && ack.understand)) { toast.error("Please confirm all three statements."); return; }
    setSubmitting(true);
    try {
      const res = await api.post("/bookings", { ...form, agreed: true });
      setConfirmation(res.data);
      window.scrollTo(0, 0);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not submit booking. Please try again.");
    } finally { setSubmitting(false); }
  };

  if (confirmation) {
    return (
      <div className="bg-[#090A0F] min-h-screen">
        <Header />
        <main className="pt-40 pb-24 px-5">
          <motion.div initial={{ opacity: 0, y: 30 }} animate={{ opacity: 1, y: 0 }} className="max-w-xl mx-auto text-center" data-testid="booking-confirmation">
            <div className="h-16 w-16 rounded-full bg-[#02AACF]/15 flex items-center justify-center mx-auto"><CalendarCheck className="text-[#02AACF]" size={30} /></div>
            <Eyebrow className="block mt-6">Booking Request Received</Eyebrow>
            <h1 className="font-display font-bold text-white text-3xl sm:text-4xl mt-3">You're one step closer.</h1>
            <p className="text-[#94A3B8] mt-4">Your LUHAI booking request has been received. We'll review your request and contact you with the next steps. A confirmation email is on its way.</p>
            {confirmation.is_custom_time && (
              <div className="mt-6 rounded-xl border border-[#f59e0b]/30 bg-[#f59e0b]/10 p-3 text-[#fbbf24] text-sm" data-testid="confirmation-custom-banner">
                Custom time requested — awaiting approval.
              </div>
            )}
            <div className="mt-8 rounded-2xl border border-white/10 bg-[#12141C] p-6 text-left space-y-3">
              {[["Booking ID", confirmation.reference], ["Package", confirmation.package_name], ["Requested Date", confirmation.shoot_date], ["Requested Time", confirmation.shoot_time ? to12h(confirmation.shoot_time) : "—"], ["Status", confirmation.status || "AWAITING CONFIRMATION"]].map(([l, v]) => (
                <div key={l} className="flex justify-between border-b border-white/5 pb-3 last:border-0">
                  <span className="text-[#64748B] text-sm font-mono2">{l}</span>
                  <span className="text-white text-sm font-semibold">{v || "—"}</span>
                </div>
              ))}
            </div>
            <LuxButton className="mt-8" onClick={() => navigate("/")} data-testid="confirmation-home">BACK TO HOME</LuxButton>
          </motion.div>
        </main>
        <Footer />
      </div>
    );
  }

  return (
    <div className="bg-[#090A0F] min-h-screen">
      <Header />
      <main className="pt-28 sm:pt-36 pb-24">
        <div className="mx-auto max-w-[900px] px-5 sm:px-8">
          <div className="flex items-center justify-between gap-3">
            <div>
              <Eyebrow>Booking</Eyebrow>
              <h1 className="font-display font-bold text-white text-3xl sm:text-4xl mt-2">{quoteMode ? "Custom Quote Request" : "Book Your Session"}</h1>
            </div>
            <button onClick={() => setQuoteMode(!quoteMode)} className="text-xs font-mono2 text-[#02AACF] hover:underline shrink-0" data-testid="toggle-quote-mode">
              {quoteMode ? "← Standard Booking" : "Need a custom quote?"}
            </button>
          </div>

          {quoteMode ? (
            <div className="mt-10"><QuoteForm onDone={(ref) => setConfirmation({ reference: ref, package_name: "Custom Quote" })} /></div>
          ) : (
            <>
              {/* progress */}
              <div className="flex items-center gap-1 sm:gap-2 mt-10" data-testid="booking-steps">
                {STEPS.map((s, i) => (
                  <div key={s} className="flex items-center flex-1">
                    <div className={`flex items-center gap-2 ${i <= step ? "text-[#02AACF]" : "text-[#64748B]"}`}>
                      <div className={`h-8 w-8 rounded-full flex items-center justify-center text-xs font-mono2 border ${i < step ? "bg-[#02AACF] border-[#02AACF] text-white" : i === step ? "border-[#02AACF]" : "border-white/15"}`}>
                        {i < step ? <Check size={14} /> : i + 1}
                      </div>
                      <span className="hidden sm:block text-xs font-mono2 tracking-wide">{s}</span>
                    </div>
                    {i < STEPS.length - 1 && <div className={`flex-1 h-px mx-2 ${i < step ? "bg-[#02AACF]" : "bg-white/10"}`} />}
                  </div>
                ))}
              </div>

              <AnimatePresence mode="wait">
                <motion.div key={step} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} transition={{ duration: 0.3 }} className="mt-10">
                  {/* STEP 0 PACKAGE */}
                  {step === 0 && (
                    <div className="grid sm:grid-cols-2 gap-4" data-testid="step-package">
                      {packages.map((p) => (
                        <button key={p.id} onClick={() => { set("package_slug", p.slug); set("package_name", p.name); set("package_price", p.price); }}
                          data-testid={`select-package-${p.slug}`}
                          className={`text-left rounded-2xl border p-6 transition-all ${form.package_slug === p.slug ? "border-[#02AACF] bg-[#0d2730] brand-glow" : "border-white/10 bg-[#12141C] hover:border-white/30"}`}>
                          <div className="flex justify-between items-start">
                            <div>
                              <Eyebrow>{p.short_name}</Eyebrow>
                              <p className="font-display font-bold text-white text-lg mt-1">{p.name}</p>
                            </div>
                            <span className="font-display font-bold text-white text-xl">{p.price ? `${p.starting_price ? "From " : ""}${p.currency}${p.price}` : "Contact"}</span>
                          </div>
                          <p className="text-[#94A3B8] text-sm mt-2">{p.tagline}</p>
                        </button>
                      ))}
                      <button onClick={() => setQuoteMode(true)} data-testid="select-package-custom"
                        className="text-left rounded-2xl border border-dashed border-white/20 p-6 bg-[#12141C] hover:border-[#02AACF]/50 transition-all">
                        <Eyebrow>Custom</Eyebrow>
                        <p className="font-display font-bold text-white text-lg mt-1">Custom Inquiry</p>
                        <p className="text-[#94A3B8] text-sm mt-2">Something outside the standard packages? Request a tailored quote.</p>
                      </button>
                    </div>
                  )}

                  {/* STEP 1 DATE */}
                  {step === 1 && (
                    <div className="flex flex-col items-center" data-testid="step-date">
                      <p className="text-[#94A3B8] text-sm mb-4">Unavailable dates are disabled automatically.</p>
                      <div className="rounded-2xl border border-white/10 bg-[#12141C] p-3">
                        <Calendar mode="single" selected={form.shoot_date ? new Date(form.shoot_date + "T00:00:00") : undefined}
                          onSelect={(d) => d && set("shoot_date", fmt(d))} disabled={disabledDays}
                          className="text-white [&_.rdp-day_selected]:bg-[#02AACF] [&_.rdp-day_selected]:text-white" />
                      </div>
                      {form.shoot_date && <p className="mt-4 font-mono2 text-sm text-[#02AACF]" data-testid="selected-date">{form.shoot_date}</p>}
                    </div>
                  )}

                  {/* STEP 2 TIME */}
                  {step === 2 && (
                    <div data-testid="step-time">
                      <p className="text-[#94A3B8] text-sm mb-5">
                        Available times for <span className="text-[#02AACF] font-mono2">{form.shoot_date}</span>
                        <span className="text-[#64748B]"> · shown in Nassau time (AM/PM)</span>
                      </p>
                      {!form.is_custom_time && (
                        slots.length === 0 ? (
                          <p className="text-[#64748B] py-10 text-center font-mono2 text-sm">No available slots for this date. Pick another date or request a custom time below.</p>
                        ) : (
                          <div className="grid grid-cols-3 sm:grid-cols-4 gap-3">
                            {slots.map((s) => (
                              <button key={s.value} onClick={() => setForm((f) => ({ ...f, shoot_time: s.value, is_custom_time: false }))} data-testid={`slot-${s.value}`}
                                className={`rounded-xl border py-3 font-mono2 text-sm transition-all ${form.shoot_time === s.value && !form.is_custom_time ? "bg-[#02AACF] border-[#02AACF] text-white" : "border-white/10 text-white/70 hover:border-[#02AACF]/50"}`}>
                                {s.label}
                              </button>
                            ))}
                          </div>
                        )
                      )}

                      <div className="mt-8 rounded-2xl border border-white/10 bg-[#12141C] p-5" data-testid="custom-time-block">
                        {!form.is_custom_time ? (
                          <div className="flex items-center justify-between gap-3 flex-wrap">
                            <div>
                              <p className="text-white font-semibold text-sm">Need a time outside these hours?</p>
                              <p className="text-[#64748B] text-xs mt-1">Request a time before 5:00 AM or after 11:00 PM — subject to approval.</p>
                            </div>
                            <LuxButton size="sm" variant="outline" onClick={() => setForm((f) => ({ ...f, is_custom_time: true, shoot_time: "" }))} data-testid="request-custom-time">Request a Custom Time</LuxButton>
                          </div>
                        ) : (
                          <div className="space-y-4">
                            <div className="flex items-center justify-between gap-3">
                              <p className="text-white font-semibold text-sm">Custom Time Request</p>
                              <button onClick={() => setForm((f) => ({ ...f, is_custom_time: false, shoot_time: "", custom_reason: "" }))} className="text-xs font-mono2 text-[#64748B] hover:text-white" data-testid="cancel-custom-time">← Back to standard times</button>
                            </div>
                            <div className="grid sm:grid-cols-2 gap-4">
                              <div>
                                <Label className="text-[#94A3B8] text-xs font-mono2 tracking-wide">Requested Time (Nassau)</Label>
                                <Input type="time" value={form.shoot_time} onChange={(e) => set("shoot_time", e.target.value)} data-testid="custom-time-input" className="mt-2 bg-[#0b0d12] border-white/10 text-white focus-visible:ring-[#02AACF]" />
                                {form.shoot_time && <p className="text-[#02AACF] text-xs font-mono2 mt-1.5" data-testid="custom-time-preview">{to12h(form.shoot_time)}</p>}
                              </div>
                            </div>
                            <TextareaField label="Reason / notes for this time (optional)" value={form.custom_reason} onChange={(e) => set("custom_reason", e.target.value)} testid="custom-time-reason" />
                            <div className="rounded-xl border border-[#f59e0b]/30 bg-[#f59e0b]/10 p-3 text-[#fbbf24] text-xs" data-testid="custom-time-notice">Custom time requested — awaiting approval. This will not be confirmed until LUHAI approves it.</div>
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  {/* STEP 3 DETAILS */}
                  {step === 3 && (
                    <div className="space-y-8" data-testid="step-details">
                      <div>
                        <p className="font-display font-bold text-white text-lg mb-4">Your Information</p>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <LabeledInput label="Full Name" required value={form.full_name} onChange={(e) => set("full_name", e.target.value)} testid="input-name" />
                          <LabeledInput label="Email" required type="email" value={form.email} onChange={(e) => set("email", e.target.value)} testid="input-email" />
                          <LabeledInput label="Phone" required value={form.phone} onChange={(e) => set("phone", e.target.value)} testid="input-phone" />
                          <div>
                            <Label className="text-[#94A3B8] text-xs font-mono2">Preferred Contact</Label>
                            <div className="mt-2"><Chips options={CONTACT_METHODS} value={form.preferred_contact} onChange={(v) => set("preferred_contact", v)} testidPrefix="contact-method" /></div>
                          </div>
                        </div>
                      </div>

                      <div>
                        <p className="font-display font-bold text-white text-lg mb-4">Photoshoot Details</p>
                        <div className="grid sm:grid-cols-2 gap-4">
                          <LabeledInput label="Shoot Location" value={form.location} onChange={(e) => set("location", e.target.value)} testid="input-location" />
                          <div>
                            <Label className="text-[#94A3B8] text-xs font-mono2">Do you have a location?</Label>
                            <div className="mt-2"><Chips options={["Yes", "No", "Need suggestions"]} value={form.has_location} onChange={(v) => set("has_location", v)} testidPrefix="has-location" /></div>
                          </div>
                          <LabeledInput label="Number of People" value={form.num_people} onChange={(e) => set("num_people", e.target.value)} testid="input-people" />
                          <LabeledInput label="Number of Outfits" value={form.num_outfits} onChange={(e) => set("num_outfits", e.target.value)} testid="input-outfits" />
                          <LabeledInput label="Instagram / Social Handle" value={form.instagram} onChange={(e) => set("instagram", e.target.value)} testid="input-instagram" />
                          <LabeledInput label="Company / Brand (if any)" value={form.business_name} onChange={(e) => set("business_name", e.target.value)} testid="input-business" />
                        </div>
                        <div className="mt-4">
                          <Label className="text-[#94A3B8] text-xs font-mono2">Type of Shoot</Label>
                          <div className="mt-2"><Chips options={SHOOT_TYPES} value={form.shoot_type} onChange={(v) => set("shoot_type", v)} testidPrefix="shoot-type" /></div>
                        </div>
                      </div>

                      <div>
                        <p className="font-display font-bold text-white text-lg mb-4">Creative Details</p>
                        <div className="space-y-4">
                          <TextareaField label="Tell me about your vision" value={form.vision} onChange={(e) => set("vision", e.target.value)} testid="input-vision" />
                          <div className="grid sm:grid-cols-2 gap-4">
                            <LabeledInput label="What look are you going for?" value={form.look} onChange={(e) => set("look", e.target.value)} testid="input-look" />
                            <LabeledInput label="Specific theme?" value={form.theme} onChange={(e) => set("theme", e.target.value)} testid="input-theme" />
                          </div>
                          <LabeledInput label="Color / wardrobe info" value={form.wardrobe} onChange={(e) => set("wardrobe", e.target.value)} testid="input-wardrobe" />
                          <TextareaField label="Inspiration links (Pinterest, IG, TikTok, Drive…)" value={form.reference_links} onChange={(e) => set("reference_links", e.target.value)} testid="input-links" />
                          <div>
                            <Label className="text-[#94A3B8] text-xs font-mono2 tracking-wide">Upload reference images (optional)</Label>
                            <div onClick={() => refInput.current.click()} onDragOver={(e) => e.preventDefault()}
                              onDrop={(e) => { e.preventDefault(); uploadRefs(e.dataTransfer.files); }}
                              className="mt-2 rounded-xl border-2 border-dashed border-white/15 hover:border-[#02AACF]/50 p-5 text-center cursor-pointer transition-colors" data-testid="reference-dropzone">
                              <ImagePlus className="mx-auto text-[#02AACF]" size={22} />
                              <p className="text-[#94A3B8] text-xs mt-2">{uploadingRef ? "Uploading…" : "Tap to upload or drag & drop moodboard images (max 8)"}</p>
                              <input ref={refInput} type="file" accept="image/*" multiple hidden onChange={(e) => uploadRefs(e.target.files)} data-testid="reference-file-input" />
                            </div>
                            {form.uploaded_inspiration.length > 0 && (
                              <div className="grid grid-cols-4 sm:grid-cols-6 gap-2 mt-3" data-testid="reference-previews">
                                {form.uploaded_inspiration.map((u) => (
                                  <div key={u} className="relative group rounded-lg overflow-hidden border border-white/10 aspect-square">
                                    <img src={u} alt="reference" className="w-full h-full object-cover" />
                                    <button type="button" onClick={() => removeRef(u)} className="absolute top-1 right-1 bg-black/70 text-white rounded-full p-0.5 opacity-0 group-hover:opacity-100 transition-opacity" data-testid="remove-reference"><X size={12} /></button>
                                  </div>
                                ))}
                              </div>
                            )}
                          </div>
                          <TextareaField label="Additional Notes" value={form.notes} onChange={(e) => set("notes", e.target.value)} testid="input-notes" />
                          <div>
                            <Label className="text-[#94A3B8] text-xs font-mono2">How did you hear about LUHAI?</Label>
                            <div className="mt-2"><Chips options={HEARD_ABOUT} value={form.heard_about} onChange={(v) => set("heard_about", v)} testidPrefix="heard" /></div>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}

                  {/* STEP 4 REVIEW */}
                  {step === 4 && (
                    <div data-testid="step-review">
                      <p className="font-display font-bold text-white text-lg mb-4">Booking Summary</p>
                      {form.is_custom_time && (
                        <div className="mb-4 rounded-xl border border-[#f59e0b]/30 bg-[#f59e0b]/10 p-3 text-[#fbbf24] text-sm" data-testid="review-custom-banner">
                          Custom time requested — awaiting approval.
                        </div>
                      )}
                      <div className="rounded-2xl border border-white/10 bg-[#12141C] p-6 space-y-3">
                        {[["Package", form.package_name], ["Price", form.package_price ? `B$${form.package_price}` : "—"], ["Date", form.shoot_date], ["Time", form.shoot_time ? `${to12h(form.shoot_time)}${form.is_custom_time ? " (Custom — awaiting approval)" : ""}` : "—"], ["Location", form.location || "TBD"], ["Name", form.full_name], ["Email", form.email], ["Phone", form.phone], ["Shoot Type", form.shoot_type]].map(([l, v]) => (
                          <div key={l} className="flex justify-between border-b border-white/5 pb-3 last:border-0">
                            <span className="text-[#64748B] text-sm font-mono2">{l}</span>
                            <span className="text-white text-sm font-semibold text-right max-w-[60%]">{v || "—"}</span>
                          </div>
                        ))}
                      </div>
                      <div className="mt-6 space-y-3">
                        {[["reviewed", "I have reviewed my booking information."], ["terms", "I agree to the booking terms."], ["understand", "I understand submitting this request does not automatically guarantee final confirmation until LUHAI approves the booking."]].map(([k, l]) => (
                          <label key={k} className="flex gap-3 items-start cursor-pointer" data-testid={`ack-${k}`}>
                            <Checkbox checked={ack[k]} onCheckedChange={(v) => setAck((a) => ({ ...a, [k]: !!v }))} className="mt-0.5 border-white/30 data-[state=checked]:bg-[#02AACF] data-[state=checked]:border-[#02AACF]" />
                            <span className="text-[#cbd5e1] text-sm">{l}</span>
                          </label>
                        ))}
                      </div>
                    </div>
                  )}
                </motion.div>
              </AnimatePresence>

              {/* nav */}
              <div className="flex justify-between mt-10">
                <LuxButton variant="ghost" onClick={back} disabled={step === 0} data-testid="booking-back">
                  <ChevronLeft size={16} /> Back
                </LuxButton>
                {step < STEPS.length - 1 ? (
                  <LuxButton onClick={next} disabled={!canNext()} data-testid="booking-next">
                    Continue <ChevronRight size={16} />
                  </LuxButton>
                ) : (
                  <LuxButton onClick={submit} disabled={submitting} data-testid="booking-submit">
                    {submitting ? "Submitting…" : "SUBMIT BOOKING REQUEST"}
                  </LuxButton>
                )}
              </div>
            </>
          )}
        </div>
      </main>
      <Footer />
    </div>
  );
}

function TextareaField({ label, value, onChange, testid }) {
  return (
    <div>
      <Label className="text-[#94A3B8] text-xs font-mono2 tracking-wide">{label}</Label>
      <Textarea value={value} onChange={onChange} rows={3} data-testid={testid}
        className="mt-2 bg-[#0b0d12] border-white/10 text-white focus-visible:ring-[#02AACF]" />
    </div>
  );
}
